import Vue from "vue";
import { getTaxisByWorkgroup } from "@/api/operations/taxis.ts";
import { generateWorkgroupCentralSelectItem } from "@/utils/callcenter";
import { VERSION } from "@/utils/version";
import { flatten, uniq } from "lodash";
import { getRequirementsByWorkgroup } from "@/api/operations/requirements.ts";

const initialState = () => ({
  sidebar: {
    opened: false,
    withoutAnimation: false,
  },
  features: {},
  version: VERSION || "(unversioned)",
  device: "desktop",
  size: "medium",
  answeredCalls: [],
  closedCalls: [],
  callCenter: null,
  selectedWorkgroupsAndCentrals: [],
  showExtensionSetupModal: false,
  serviceFormTabIndexes: null,
  selectedProvidedServicesFilter: [],
  portalsZoneNeedsRecalculation: false,
  importServicesDrawer: false,
});

const state = initialState();

const mutations = {
  TOGGLE_SIDEBAR: (state) => {
    state.sidebar.opened = !state.sidebar.opened;
    state.sidebar.withoutAnimation = false;
  },
  CLOSE_SIDEBAR: (state, withoutAnimation) => {
    state.sidebar.opened = false;
    state.sidebar.withoutAnimation = withoutAnimation;
  },
  TOGGLE_DEVICE: (state, device) => {
    state.device = device;
  },
  SET_SIZE: (state, size) => {
    state.size = size;
  },
  ADD_ANSWERED_CALL: (state, call) => {
    state.answeredCalls.push(call);
  },
  DELETE_ANSWERED_CALL: (state, call) => {
    state.answeredCalls = state.answeredCalls.filter((c) => c !== call);
  },
  ADD_CLOSED_CALL: (state, call) => {
    state.closedCalls.push(call);
  },
  SET_CALL_CENTER: (state, callCenter) => {
    state.callCenter = callCenter;
  },
  SET_SELECTED_WORKGROUPS_AND_CENTRALS: (state, workgroupsAndCentrals) => {
    state.selectedWorkgroupsAndCentrals = workgroupsAndCentrals;
  },
  SET_SELECTED_PROVIDED_SERVICES_FILTER: (state, filter) => {
    state.selectedProvidedServicesFilter = filter;
  },
  SET_SHOW_EXTENSION_SETUP_MODAL: (state, show) => {
    state.showExtensionSetupModal = show;
  },
  RESET: (state) => {
    const emptyState = initialState();
    Object.keys(emptyState).forEach((k) => {
      state[k] = emptyState[k];
    });
  },
  NEW_VERSION_AVAILABLE: (state, version) => {
    state.newVersionAvailable = version;
  },
  SET_SERVICE_FORM_TAB_INDEXES: (state, tabIndexes) => {
    state.serviceFormTabIndexes = tabIndexes;
  },
  SET_WORKGROUP_TAXIS: (state, { wgId, taxis }) => {
    const index = state.callCenter.wgs.findIndex((wg) => wg.id === wgId);
    const wgData = state.callCenter.wgs[index];
    wgData.taxis = taxis;
    Vue.set(state.callCenter.wgs, index, wgData);
  },
  SET_WORKGROUP_REQUIREMENTS: (state, { wgId, requirements }) => {
    const index = state.callCenter.wgs.findIndex((wg) => wg.id === wgId);
    const wgData = state.callCenter.wgs[index];
    wgData.requirements = requirements;
    Vue.set(state.callCenter.wgs, index, wgData);
  },
  SET_FEATURES: (state, value) => {
    state.features = value;
  },
  SET_PORTALS_ZONE_NEEDS_RECALCULATION: (state, value) => {
    state.portalsZoneNeedsRecalculation = value;
  },
  SET_IMPORT_SERVICES_DRAWER: (state, value) => {
    state.importServicesDrawer = value;
  },
};

const actions = {
  toggleSideBar({ commit }) {
    commit("TOGGLE_SIDEBAR");
  },
  closeSideBar({ commit }, { withoutAnimation }) {
    commit("CLOSE_SIDEBAR", withoutAnimation);
  },
  toggleDevice({ commit }, device) {
    commit("TOGGLE_DEVICE", device);
  },
  setSize({ commit }, size) {
    commit("SET_SIZE", size);
  },
  addAnsweredCall({ commit }, call) {
    commit("ADD_ANSWERED_CALL", call);
  },
  deleteAnsweredCall({ commit }, call) {
    commit("DELETE_ANSWERED_CALL", call);
  },
  addClosedCall({ commit }, call) {
    commit("ADD_CLOSED_CALL", call);
  },
  setShowExtensionSetupModal({ commit }, show) {
    commit("SET_SHOW_EXTENSION_SETUP_MODAL", show);
  },
  setCallCenter({ commit, state, dispatch }, callCenter) {
    commit("SET_CALL_CENTER", callCenter);
    dispatch("sanitizeSelectedWorkgroupsAndCentrals");
    if (state.selectedWorkgroupsAndCentrals.length === 0) {
      commit(
        "SET_SELECTED_WORKGROUPS_AND_CENTRALS",
        callCenter?.wgs.map(({ id }) => generateWorkgroupCentralSelectItem(id))
      );
    }
  },
  sanitizeSelectedWorkgroupsAndCentrals({ commit, state }) {
    const currentAvailableWgIds = state.callCenter.wgs.map(({ id }) => id);
    const sanitizedSelectedWorkgroupsAndCentrals = state.selectedWorkgroupsAndCentrals.filter(
      (item) => currentAvailableWgIds.includes(item.wgId)
    );
    commit("SET_SELECTED_WORKGROUPS_AND_CENTRALS", sanitizedSelectedWorkgroupsAndCentrals);
  },
  setSelectedWorkgroupsAndCentrals({ commit }, workgroupsAndCentrals) {
    commit("SET_SELECTED_WORKGROUPS_AND_CENTRALS", workgroupsAndCentrals);
  },
  setSelectedProvidedServicesFilter({ commit }, filter) {
    commit("SET_SELECTED_PROVIDED_SERVICES_FILTER", filter);
  },
  reset({ commit }) {
    commit("RESET");
  },
  newVersionAvailable({ commit }, version) {
    commit("NEW_VERSION_AVAILABLE", version);
  },
  setServiceFormTabIndexes({ commit }, tabIndexes) {
    commit("SET_SERVICE_FORM_TAB_INDEXES", tabIndexes);
  },
  async reloadWgTaxis({ commit }, wgId) {
    const taxis = await getTaxisByWorkgroup(wgId);
    commit("SET_WORKGROUP_TAXIS", { wgId, taxis });
  },
  async reloadWgRequirements({ commit }, wgId) {
    const requirements = await getRequirementsByWorkgroup(wgId);
    commit("SET_WORKGROUP_REQUIREMENTS", { wgId, requirements });
  },
  setPortalsZoneNeedsRecalculation({ commit }, value) {
    commit("SET_PORTALS_ZONE_NEEDS_RECALCULATION", value);
  },
  setImportServicesDrawer({ commit }, value) {
    commit("SET_IMPORT_SERVICES_DRAWER", value);
  },
};

const getters = {
  callCenter(state) {
    return state.callCenter;
  },
  workgroups(state) {
    return state.callCenter?.wgs || [];
  },
  defaultWorkgroup(_, getters) {
    const wg = getters.workgroups[0];
    if (wg) {
      return wg;
    }
    return null;
  },
  defaultWorkgroupCoords(_, getters) {
    return getters.defaultWorkgroup?.defaultLocCoords;
  },
  defaultWorkgroupRequirements(_, getters) {
    return getters.defaultWorkgroup.requirements.sort((a, b) => {
      return a.order - b.order;
    });
  },
  centralsByWorkgroupId(_, getters) {
    return (wgId) => {
      return getters.workgroups.find((wg) => wg.id === wgId)?.centrals || [];
    };
  },
  centrals(_, getters) {
    return flatten(getters.workgroups.map((wg) => wg.centrals));
  },
  taxis(_, getters) {
    return getters.workgroups.map((wg) => wg.centrals.map((central) => central.taxis)).flat(2);
  },
  wgTaxis(_, getters) {
    return (wgId) =>
      getters.workgroups.find((wg) => wg.id === wgId).taxis ||
      getters.workgroups
        .find((wg) => wg.id === wgId)
        ?.centrals.map((central) => central.taxis)
        .flat(2) ||
      [];
  },
  serviceFormTabIndexes(state) {
    return state.serviceFormTabIndexes;
  },
  selectedWorkgroupsAndCentrals(state) {
    return state.selectedWorkgroupsAndCentrals;
  },
  selectedWorkgroupsIds(state) {
    return state.selectedWorkgroupsAndCentrals
      .filter((item) => !item.centralId)
      .map((wg) => wg.wgId);
  },
  selectedCentralsIds(state) {
    return state.selectedWorkgroupsAndCentrals
      .filter((item) => item.centralId)
      .map((wg) => wg.centralId);
  },
  selectedWorkgroupsIdsIncludingSelectedCentrals(state) {
    return uniq(state.selectedWorkgroupsAndCentrals.map((wg) => wg.wgId));
  },
  defaultSelectedWorkgroups(_, getters) {
    return [generateWorkgroupCentralSelectItem(getters.defaultWorkgroup.id)];
  },
  defaultSelectedWorkgroupAndCentral(_, getters) {
    return [
      generateWorkgroupCentralSelectItem(
        getters.defaultWorkgroup.id,
        getters.defaultWorkgroup.centrals[0].id
      ),
    ];
  },
  showExtensionSetupModal(state) {
    return state.showExtensionSetupModal;
  },
  isNewVersionAvailable(state) {
    return !!state.newVersionAvailable;
  },
  version(state) {
    return state.version;
  },
  selectedProvidedServicesFilter(state) {
    return state.selectedProvidedServicesFilter;
  },
  portalsZoneNeedsRecalculation(state) {
    return state.portalsZoneNeedsRecalculation;
  },
  importServicesDrawer(state) {
    return state.importServicesDrawer;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
